import { useState, useEffect, useRef, useCallback } from 'react';
import { track } from '@/lib/analytics';

/**
 * 3D-Grundriss showcase section — modeled after the Hero section.
 *
 * - Always shows a Vorher / Nachher pair (left side, image).
 * - Clickable Vorher/Nachher tabs at the top of the image, with a progress bar
 *   that fills while the state is active. After the bar fills, it advances:
 *     Vorher → Nachher → next pair → Vorher → …
 * - Clickable thumbnails of the "Vorher" floor plans below the image. Clicking
 *   a thumb jumps to that pair (state resets to Vorher).
 * - Right side: marketing copy + CTA. CTA wires to the same handler as the Hero
 *   so the same /signup funnel applies.
 */

interface FloorPlanSectionProps {
  onCtaClick?: () => void;
}

const pairs: { vorher: string; nachher: string; label: string }[] = [
  {
    vorher: '/landing/grundriss/ES_IMG_Grundriss1_vorher.jpg',
    nachher: '/landing/grundriss/ES_IMG_Grundriss1_3D.jpg',
    label: 'Wohnung 1',
  },
  {
    vorher: '/landing/grundriss/ES_IMG_Grundriss2_vorher.jpg',
    nachher: '/landing/grundriss/ES_IMG_Grundriss2_3D.jpg',
    label: 'Wohnung 2',
  },
  {
    vorher: '/landing/grundriss/ES_IMG_Grundriss3_vorher.jpg',
    nachher: '/landing/grundriss/ES_IMG_Grundriss3_3D.jpg',
    label: 'Wohnung 3',
  },
];

const STATE_DURATION = 3200; // ms each Vorher/Nachher state holds — matches CSS animation

export const FloorPlanSection: React.FC<FloorPlanSectionProps> = ({ onCtaClick }) => {
  const [pairIdx, setPairIdx] = useState(0);
  const [state, setState] = useState<'vorher' | 'nachher'>('vorher');
  const [tabKey, setTabKey] = useState(0); // restart progress animation
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = useCallback((nextPairIdx: number, nextState: 'vorher' | 'nachher' = 'vorher') => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPairIdx(nextPairIdx);
    setState(nextState);
    setTabKey(k => k + 1);
  }, []);

  // Auto-advance: vorher → nachher → next pair → …
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (state === 'vorher') {
        goTo(pairIdx, 'nachher');
      } else {
        goTo((pairIdx + 1) % pairs.length, 'vorher');
      }
    }, STATE_DURATION);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [pairIdx, state, goTo]);

  return (
    <section className="lp-floorplan-section" id="floorplan">
      <div className="lp-floorplan-inner">
        {/* Image side */}
        <div className="lp-floorplan-image-container">
          {/* Vorher / Nachher tabs with progress */}
          <div className="lp-floorplan-tabs">
            {(['vorher', 'nachher'] as const).map(s => {
              const isActive = state === s;
              return (
                <button
                  type="button"
                  key={`${s}-${tabKey}-${isActive}`}
                  className={`lp-floorplan-tab${isActive ? ' active' : ''}`}
                  onClick={() => goTo(pairIdx, s)}
                >
                  <span className="lp-progress-fill" />
                  <span>{s === 'vorher' ? 'Vorher' : 'Nachher'}</span>
                </button>
              );
            })}
          </div>

          {/* Stacked images for crossfade */}
          <div className="lp-floorplan-stage">
            {pairs.map((pair, i) => (
              <div
                key={i}
                className={`lp-floorplan-slide${i === pairIdx ? ' active' : ''}`}
                aria-hidden={i !== pairIdx}
              >
                <img
                  src={state === 'vorher' || i !== pairIdx ? pair.vorher : pair.nachher}
                  alt={`Grundriss ${pair.label} — ${state === 'vorher' ? 'Vorher' : '3D'}`}
                />
              </div>
            ))}
          </div>

          {/* Thumb preview row — always shows the Vorher of every pair */}
          <div className="lp-floorplan-thumbs">
            {pairs.map((pair, i) => (
              <button
                key={i}
                type="button"
                className={`lp-floorplan-thumb${i === pairIdx ? ' active' : ''}`}
                onClick={() => goTo(i, 'vorher')}
                aria-label={`Grundriss ${i + 1} anzeigen`}
              >
                <img src={pair.vorher} alt="" />
              </button>
            ))}
          </div>
        </div>

        {/* Text side */}
        <div className="lp-floorplan-content">
          <div className="lp-floorplan-eyebrow">3D-Grundrisse aus 2D-Skizzen</div>
          <h2>
            Hochwertige Grundrisse,
            <br />
            die Eindruck machen
          </h2>
          <p className="lp-floorplan-description">
            Verwandle flache 2D-Zeichnungen in fotorealistische, möblierte 3D-Grundrisse.
            Perfekt, um deine Immobilie im Exposé hochwertig darzustellen — und mit nur
            wenigen Klicks einzelne Wohneinheiten aus einem Flächengrundriss ausschneiden.
          </p>
          <ul className="lp-floorplan-bullets">
            <li>
              <span className="lp-floorplan-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </span>
              Aus Skizze, Foto oder PDF — flache 2D-Pläne werden zu möblierten 3D-Visualisierungen
            </li>
            <li>
              <span className="lp-floorplan-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </span>
              Wohneinheiten aus einem Flächengrundriss ausschneiden und einzeln darstellen
            </li>
            <li>
              <span className="lp-floorplan-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </span>
              Sofort fürs Exposé exportieren — kein zusätzlicher Tool-Wechsel
            </li>
          </ul>
          <div className="lp-floorplan-cta">
            <button
              className="lp-btn-primary inline"
              onClick={() => { track('Landing CTA Clicked', { location: 'floorplan' }); onCtaClick?.(); }}
            >
              Jetzt kostenlos testen
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
              </svg>
            </button>
            <p className="lp-floorplan-cta-note">
              Registriere dich jetzt und erhalte <strong>10 Credits gratis</strong>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
